import { supabase } from '@/lib/supabase';
import { Lang } from '@/data/constants';
import { UserType } from '@/data/constants';

/**
 * ACCOUNT STATE HELPERS — email verification + account-type repair.
 *
 * ONE source of truth for both fixes shipped on 2026-09-22:
 *   1. "I never got a confirmation email"  -> resendConfirmation()
 *   2. "I signed up as a contractor and got a homeowner dashboard"
 *      -> roleRepairStatus() / repairRole()
 *
 * Everything here talks to SERVER functions that re-derive the caller from the
 * JWT (account-verification, account-role-repair). Nothing here grants itself
 * anything; the browser only asks and renders the answer.
 */

/** Seconds a user must wait between confirmation-email sends. */
export const RESEND_COOLDOWN_SECONDS = 60;

export type VerificationState = 'unverified' | 'legacy_unverified' | 'verified' | string;

export interface VerificationStatus {
  ok: boolean;
  email?: string;
  state: VerificationState;
  verified: boolean;
  /** Accounts created before confirmation was enforced. Never newly issued. */
  legacy: boolean;
  phoneVerified: boolean;
  accountActive: boolean;
}

export const EMPTY_VERIFICATION: VerificationStatus = {
  ok: false,
  state: 'unverified',
  verified: false,
  legacy: false,
  phoneVerified: false,
  accountActive: true,
};

const asStatus = (d: Record<string, unknown> | null | undefined): VerificationStatus => {
  const state = String(d?.email_verification_state || 'unverified');
  return {
    ok: Boolean(d?.ok),
    email: (d?.email as string) || undefined,
    state,
    verified: state === 'verified' || d?.email_verified === true,
    legacy: state === 'legacy_unverified',
    phoneVerified: d?.phone_verified === true,
    accountActive: d?.account_active !== false,
  };
};

/**
 * Current verification state for the signed-in account.
 *
 * `sync` first asks the server to promote the account when the auth provider
 * has ALREADY confirmed the mailbox after the enforcement cutoff — that
 * promotion is server-side and based on the provider's own timestamp, never on
 * anything the browser claims.
 */
export const fetchVerification = async (sync = true): Promise<VerificationStatus> => {
  try {
    if (sync) {
      await supabase.functions.invoke('account-verification', { body: { action: 'sync_email' } });
    }
    const { data, error } = await supabase.functions.invoke('account-verification', {
      body: { action: 'status' },
    });
    if (error || !data) return EMPTY_VERIFICATION;
    return asStatus(data as Record<string, unknown>);
  } catch {
    return EMPTY_VERIFICATION;
  }
};

export interface ResendResult {
  ok: boolean;
  /** 'provider' = auth confirmation mail, 'account' = our own account email. */
  via?: 'provider' | 'account';
  /** Set when the mailbox was already confirmed, so there is nothing to confirm. */
  alreadyConfirmed?: boolean;
  rateLimited?: boolean;
  retryAfter?: number;
  error?: string;
}

/**
 * Re-send the confirmation email for the SIGNED-IN account.
 *
 * Step 1 asks account-verification, which performs the provider resend
 * (auth resend, type: 'signup') behind a server-side rate limit of
 * 3/hour/user and 10/hour/address, and writes a security-audit row.
 *
 * Step 2 covers the real production situation found while investigating this
 * report: this project's auth provider AUTO-CONFIRMS new addresses, so a
 * provider "signup confirmation" mail does not exist and the resend is
 * rejected with "already confirmed". In that case the account genuinely has
 * nothing to confirm — but the person still never received anything, which is
 * exactly the complaint. So we send the real account email (a GIGGONE-branded
 * "your account is active" message, logged in mkt_email_log and mirrored to
 * the dashboard) instead of pretending a mail went out.
 */
export const resendConfirmation = async (
  email: string,
  lang: Lang,
  userId?: string,
  name?: string,
): Promise<ResendResult> => {
  try {
    const { data, error } = await supabase.functions.invoke('account-verification', {
      body: { action: 'resend_email' },
    });

    if (!error && data?.ok) return { ok: true, via: 'provider' };

    const payload = (data || {}) as Record<string, unknown>;
    if (payload.error === 'rate_limited') {
      return { ok: false, rateLimited: true, retryAfter: Number(payload.retry_after || 0) };
    }

    // Mailbox already confirmed by the provider -> send the account email.
    const sent = await supabase.functions.invoke('send-transactional-email', {
      body: {
        template: 'account_activated',
        to: email,
        language: lang,
        userId: userId || null,
        context: { name: name || undefined },
      },
    });
    if (!sent.error && sent.data?.success) {
      return { ok: true, via: 'account', alreadyConfirmed: true };
    }
    return { ok: false, error: String(payload.error || sent.error?.message || 'send_failed') };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
};

/**
 * Does this sign-in failure mean "the address exists but is not confirmed"?
 *
 * The auth provider answers unconfirmed sign-ins with a distinct message, and
 * we must not flatten it into "email or password is incorrect" — that is what
 * left people with no way to recover on their own.
 */
export const isUnconfirmedError = (message?: string | null): boolean => {
  const m = (message || '').toLowerCase();
  return (
    m.includes('email not confirmed') ||
    m.includes('email_not_confirmed') ||
    m.includes('not confirmed') ||
    m.includes('confirm your email') ||
    m.includes('correo no confirmado')
  );
};

/* ------------------------------------------------------------------ */
/* ACCOUNT TYPE REPAIR                                                 */
/* ------------------------------------------------------------------ */

export interface RoleRepairStatus {
  looksMisroled: boolean;
  repairAvailable: boolean;
  alreadyRepaired: boolean;
  evidence: string[];
  userType?: UserType;
}

export const EMPTY_ROLE_REPAIR: RoleRepairStatus = {
  looksMisroled: false,
  repairAvailable: false,
  alreadyRepaired: false,
  evidence: [],
};

/**
 * Detects the signup race that stored a company registration as a project
 * owner (company_name / contact_name / trades present, user_type 'homeowner').
 * The server decides — the browser never guesses.
 */
export const roleRepairStatus = async (): Promise<RoleRepairStatus> => {
  try {
    const { data, error } = await supabase.functions.invoke('account-role-repair', {
      body: { action: 'status' },
    });
    if (error || !data?.ok) return EMPTY_ROLE_REPAIR;
    return {
      looksMisroled: data.looks_misroled === true,
      repairAvailable: data.repair_available === true,
      alreadyRepaired: data.already_repaired === true,
      evidence: Array.isArray(data.evidence) ? data.evidence : [],
      userType: data.user_type as UserType,
    };
  } catch {
    return EMPTY_ROLE_REPAIR;
  }
};

export const repairRole = async (
  userType: Extract<UserType, 'gc' | 'subcontractor'>,
): Promise<{ ok: boolean; error?: string }> => {
  try {
    const { data, error } = await supabase.functions.invoke('account-role-repair', {
      body: { action: 'repair', user_type: userType },
    });
    if (error) return { ok: false, error: error.message };
    if (data?.ok) return { ok: true };
    return { ok: false, error: String(data?.error || 'repair_failed') };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
};
